import { Injectable } from '@angular/core';
import { ApiCrudService } from '../../../../core/services/api/api-crud.service';
import { StationsModel, StationTypeModel } from '../../models/stations';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable, catchError } from 'rxjs';
import { StationsAdapter } from '../../adapters/stations';
import { StationConnectionAdapter } from '../../adapters/station-connection';
import { PaginatedResponse } from '../../../../core/services/api/paginated-response';
import { StationDataManagementModel } from '../../models/station-data-management';
import { StationDataManagementAdapter } from '../../adapters/station-data-management';
import { environment } from '@env/environment';

const BASE_URL = environment.apiBaseUrl + '/weather-station/stations';

@Injectable({
  providedIn: 'root',
})
export class StationApiService extends ApiCrudService<StationsModel> {
  private readonly weatherDataApiUrl = environment.apiBaseUrl;

  constructor(http: HttpClient) {
    super(http, BASE_URL);
  }

  override getAll(): Observable<StationsModel[]> {
    return this.http
      .get<any>(this.baseUrl)
      .pipe(map((response: any) => response.data.map(StationsAdapter.fromApi)));
  }

  getPaginated(
    page: number,
    size: number,
    sortField: string,
    sortDirection: string,
  ): Observable<PaginatedResponse<StationsModel>> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', `${sortField},${sortDirection}`);

    return this.http.get<any>(this.baseUrl + '/paginated', { params }).pipe(
      map((response) => {
        if (response?.data) {
          const data = response.data;
          return {
            content: data.content || [],
            totalElements: data.totalElements || 0,
            totalPages: data.totalPages || 0,
            size: data.size || size,
            number: data.number ?? page,
            hasNext: data.hasNext || false,
            hasPrevious: data.hasPrevious || false,
          } as PaginatedResponse<StationsModel>;
        }

        return {
          content: [],
          totalElements: 0,
          totalPages: 0,
          size: size,
          number: page,
          hasNext: false,
          hasPrevious: false,
        } as PaginatedResponse<StationsModel>;
      }),
    );
  }

  override getById(id: number | string): Observable<StationsModel> {
    return this.http
      .get<any>(`${this.baseUrl}/${id}`)
      .pipe(map((response: any) => StationsAdapter.fromApi(response.data)));
  }

  override create(data: StationsModel): Observable<StationsModel> {
    return this.http
      .post<any>(this.baseUrl, data, {
        headers: {
          'X-Show-Success': 'create',
          'X-Entity-Name': 'estação',
          'X-Entity-Article': 'da',
        },
      })
      .pipe(map((response: any) => StationsAdapter.fromApi(response.data)));
  }

  override delete(id: number | string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`, {
      headers: {
        'X-Show-Success': 'delete',
        'X-Entity-Name': 'estação',
        'X-Entity-Article': 'da',
      },
    });
  }

  override update(id: number | string, data: any): Observable<StationsModel> {
    return this.http
      .put<any>(`${this.baseUrl}/${id}`, data, {
        headers: {
          'X-Show-Success': 'update',
          'X-Entity-Name': 'estação',
          'X-Entity-Article': 'da',
        },
      })
      .pipe(map((response: any) => StationsAdapter.fromApi(response.data)));
  }

  getTypes(): Observable<StationTypeModel[]> {
    return this.http
      .get<any>(environment.apiBaseUrl + '/weather-station/types')
      .pipe(map((res) => res.data));
  }

  /**
   Busca lista paginada de conexões das estações meteorológicas.
   Permite ordenação pelos campos: weatherStationType, weatherStationDescription, lastHourData, offlineTimeHours, connectionStatus
   */
  getStationsConnectionPaginated(
    page: number,
    size: number,
    sortField: string,
    sortDirection: string,
  ): Observable<PaginatedResponse<any>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    const validSortFields = [
      'weatherStationType',
      'weatherStationDescription',
      'lastHourData',
      'offlineTimeHours',
      'connectionStatus',
    ];
    if (validSortFields.includes(sortField)) {
      params = params.set('sort', `${sortField},${sortDirection}`);
    }

    const url = `${environment.apiBaseUrl}/weather-station/data/last/paginated`;
    return this.http.get<any>(url, { params }).pipe(
      map((response) => {
        if (response?.data) {
          const data = response.data;
          return {
            content: (data.content || []).map(StationConnectionAdapter.fromApi),
            totalElements: data.totalElements || 0,
            totalPages: data.totalPages || 0,
            number: data.currentPage ?? 0,
            size: size,
            hasNext: data.hasNext || false,
            hasPrevious: data.hasPrevious || false,
          } as PaginatedResponse<any>;
        }

        return {
          content: [],
          totalElements: 0,
          totalPages: 0,
          number: page,
          size: size,
          hasNext: false,
          hasPrevious: false,
        } as PaginatedResponse<any>;
      }),
    );
  }

  getDailyData(
    stationId: string,
    page: number = 0,
    pageSize: number = 10,
    sort: string = 'generationDate,desc',
  ): Observable<PaginatedResponse<StationDataManagementModel>> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', pageSize.toString())
      .set('sort', sort);

    return this.http
      .get<any>(
        `${this.weatherDataApiUrl}/weather-station/data/daily/${stationId}`,
        { params },
      )
      .pipe(
        map((response) => {
          if (response.data) {
            const data = response.data;
            return {
              content: data.content || [],
              totalElements: data.totalElements || 0,
              totalPages: data.totalPages || 0,
              size: pageSize,
              number: data.currentPage ?? page,
              hasNext: data.hasNext || false,
              hasPrevious: data.hasPrevious || false,
            } as PaginatedResponse<StationDataManagementModel>;
          }

          return {
            content: [],
            totalElements: 0,
            totalPages: 0,
            size: pageSize,
            number: page,
            hasNext: false,
            hasPrevious: false,
          } as PaginatedResponse<StationDataManagementModel>;
        }),
        catchError((error) => {
          console.error('Erro em getDailyData:', error);
          throw error;
        }),
      );
  }

  getDailyDataByPeriod(
    stationId: string,
    startDate: string,
    endDate: string,
    page: number = 0,
    size: number = 10,
    sort: string = 'generationDate,desc',
  ): Observable<PaginatedResponse<StationDataManagementModel>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort);

    // Adicionar apenas as datas que estão preenchidas
    if (startDate?.trim()) {
      params = params.set('startDate', startDate);
    }

    if (endDate?.trim()) {
      params = params.set('endDate', endDate);
    }

    return this.http
      .get<any>(
        `${this.weatherDataApiUrl}/weather-station/data/daily/${stationId}`,
        { params },
      )
      .pipe(
        map((response) => {
          if (response?.data && typeof response.data === 'object') {
            const data = response.data;
            return {
              content: data.content || [],
              totalElements: data.totalElements || 0,
              totalPages: data.totalPages || 0,
              size: size,
              number: data.currentPage ?? page,
              hasNext: data.hasNext || false,
              hasPrevious: data.hasPrevious || false,
            } as PaginatedResponse<StationDataManagementModel>;
          }

          return {
            content: [],
            totalElements: 0,
            totalPages: 0,
            size: size,
            number: page,
            hasNext: false,
            hasPrevious: false,
          } as PaginatedResponse<StationDataManagementModel>;
        }),
        catchError((error) => {
          console.error('Erro em getDailyDataByPeriod:', error);
          throw error;
        }),
      );
  }

  /**
   * Busca dados específicos de uma estação por data
   * Utiliza startDate e endDate com a mesma data para buscar o registro exato
   */
  getStationDataByDate(
    stationId: string,
    date: string,
  ): Observable<StationDataManagementModel | null> {
    const params = new HttpParams().set('startDate', date).set('endDate', date);

    const apiUrl = `${this.weatherDataApiUrl}/weather-station/data/daily/${stationId}`;

    return this.http.get<any>(apiUrl, { params }).pipe(
      map((response) => {
        if (
          response?.data?.content &&
          Array.isArray(response.data.content) &&
          response.data.content.length > 0
        ) {
          return StationDataManagementAdapter.fromApi(response.data.content[0]);
        }

        return null;
      }),
      catchError((error) => {
        console.error('Erro ao buscar dado meteorológico por data:', error);
        throw error;
      }),
    );
  }

  /**
   * Retorna a quantidade total de estações sem comunicação via endpoint dedicado.
   */
  getDisconnectedStationsCount(): Observable<number> {
    // Adiciona header para desabilitar loading global
    const headers = { 'X-Skip-Loading': 'true' };

    return this.http
      .get<{
        data: { totalStationsWithoutCommunication: number };
      }>(`${environment.apiBaseUrl}/weather-station/data/last/count`, {
        headers,
      })
      .pipe(
        map(
          (response) => response?.data?.totalStationsWithoutCommunication ?? 0,
        ),
        catchError((error) => {
          console.error(
            'Erro ao buscar total de estações sem comunicação:',
            error,
          );
          return [0]; // retorna zero em caso de erro para manter usabilidade
        }),
      );
  }

  /**
   * Atualiza dados meteorológicos diários existentes
   */
  updateDailyData(
    data: StationDataManagementModel,
  ): Observable<StationDataManagementModel> {
    return this.http
      .put<any>(`${this.weatherDataApiUrl}/weather-station/data/daily`, data, {
        headers: {
          'X-Show-Success': 'update',
          'X-Entity-Name': 'dados meteorológicos',
          'X-Entity-Article': 'dos',
        },
      })
      .pipe(
        map((response: any) =>
          StationDataManagementAdapter.fromApi(response.data),
        ),
        catchError((error) => {
          console.error('Erro ao atualizar dados meteorológicos:', error);
          throw error;
        }),
      );
  }

  /**
   * Consolida dados diários de uma estação para uma data específica
   */
  consolidateDailyData(stationId: string, date: string): Observable<any> {
    const params = new HttpParams().set('date', date);

    return this.http
      .post<any>(
        `${this.weatherDataApiUrl}/weather-station/data/daily/consolidate/${stationId}`,
        null,
        { params },
      )
      .pipe(
        map((response: any) => response.data || response),
        catchError((error) => {
          console.error('Erro ao consolidar dados diários:', error);
          throw error;
        }),
      );
  }
}
